import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ShieldAlert, Quote, ChevronDown, ChevronUp, Sparkles, Ruler } from "lucide-react";

/**
 * Clauses a risque d'une analyse avant signature.
 *
 * Les clauses Critical sont toujours visibles ; High/Medium/Low sont repliees
 * derriere une bascule — traduction UI de la regle produit "alerte proactive
 * seulement si Critical, le reste consultable a la demande".
 *
 * Couche visuelle "travail" (zinc), pas la couche Guardian navy — cf. la
 * Two-Layer Rule de DESIGN.md.
 */
export const SEVERITY_RANK = { Critical: 0, High: 1, Medium: 2, Low: 3 };

export const SEVERITY_PILL_CLASS = {
  Critical: "bg-rose-50 text-rose-700 border-rose-200",
  High: "bg-orange-50 text-orange-700 border-orange-200",
  Medium: "bg-amber-50 text-amber-800 border-amber-200",
  Low: "bg-zinc-50 text-zinc-600 border-zinc-200",
};

function _usable(list) {
  return (Array.isArray(list) ? list : []).filter(
    (c) => c && typeof c === "object" && typeof c.title === "string" && c.title.trim(),
  );
}

function _sort(list) {
  return [...list].sort((a, b) => {
    const ra = SEVERITY_RANK[a.severity] ?? 9;
    const rb = SEVERITY_RANK[b.severity] ?? 9;
    return ra !== rb ? ra - rb : a.title.localeCompare(b.title);
  });
}

export function partitionClauses(clauses) {
  const usable = _usable(clauses);
  return {
    critical: _sort(usable.filter((c) => c.severity === "Critical")),
    others: _sort(usable.filter((c) => c.severity !== "Critical")),
  };
}

export function worstSeverity(clauses) {
  const ranks = _usable(clauses)
    .map((c) => SEVERITY_RANK[c.severity])
    .filter((r) => r !== undefined);
  if (!ranks.length) return null;
  const best = Math.min(...ranks);
  return Object.keys(SEVERITY_RANK).find((k) => SEVERITY_RANK[k] === best) || null;
}

function ClauseCard({ clause, index }) {
  const { t } = useTranslation("pre_signature");
  const pill = SEVERITY_PILL_CLASS[clause.severity] || SEVERITY_PILL_CLASS.Low;
  return (
    <article
      data-testid={`clause-finding-${index}`}
      className="rounded-xl border border-zinc-200 bg-white p-4 space-y-3"
    >
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-medium mb-1">
            {t(`clause_type.${clause.clause_type}`, { defaultValue: t("clause_type.other") })}
          </p>
          <h4 className="text-sm font-semibold text-zinc-950 leading-snug">{clause.title}</h4>
        </div>
        <span
          data-testid={`clause-severity-${index}`}
          className={`shrink-0 text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full border ${pill}`}
        >
          {t(`severity.${clause.severity}`, { defaultValue: clause.severity })}
        </span>
      </header>

      <div>
        <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium mb-1 flex items-center gap-1.5">
          <Quote size={10} strokeWidth={2} /> {t("clause.quote_label")}
        </p>
        {clause.quote ? (
          <blockquote
            data-testid={`clause-quote-${index}`}
            className="border-l-2 border-zinc-300 pl-3 text-sm text-zinc-700 italic leading-relaxed"
          >
            {clause.quote}
          </blockquote>
        ) : (
          <p className="text-xs text-zinc-400 italic">{t("clause.no_quote")}</p>
        )}
      </div>

      {clause.plain_language && (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium mb-1">
            {t("clause.what_it_means")}
          </p>
          <p className="text-sm text-zinc-800 leading-relaxed">{clause.plain_language}</p>
        </div>
      )}

      {clause.why_it_matters && (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium mb-1">
            {t("clause.why_it_matters")}
          </p>
          <p className="text-sm text-zinc-800 leading-relaxed">{clause.why_it_matters}</p>
        </div>
      )}

      {clause.suggested_action && (
        <div className="rounded-lg bg-zinc-50 border border-zinc-200 p-3">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium mb-1">
            {t("clause.suggested_action")}
          </p>
          <p className="text-sm text-zinc-900 font-medium leading-relaxed">
            {clause.suggested_action}
          </p>
        </div>
      )}

      <footer className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-400">
        {clause.source_rule_key ? (
          <><Ruler size={10} strokeWidth={2} /> {t("clause.source_rule")}</>
        ) : (
          <><Sparkles size={10} strokeWidth={2} /> {t("clause.source_reading")}</>
        )}
        {clause.source_path && <span className="font-mono normal-case">· {clause.source_path}</span>}
      </footer>
    </article>
  );
}

export default function ClauseFindings({ clauses }) {
  const { t } = useTranslation("pre_signature");
  const [showOthers, setShowOthers] = useState(false);
  const { critical, others } = useMemo(() => partitionClauses(clauses), [clauses]);
  const total = critical.length + others.length;

  return (
    <section data-testid="clause-findings" className="space-y-3">
      <div className="flex items-center gap-2">
        <ShieldAlert size={14} className="text-zinc-700" strokeWidth={1.75} />
        <h3 className="font-display font-semibold text-base tracking-tight">
          {t("clause.section_title")}
          <span className="text-xs text-zinc-500 font-normal ml-2" data-testid="clause-findings-count">
            {t("clause.section_count", { count: total })}
          </span>
        </h3>
      </div>

      {total === 0 && (
        <p className="text-sm text-zinc-500 italic" data-testid="clause-findings-empty">
          {t("clause.none")}
        </p>
      )}

      {total > 0 && critical.length === 0 && (
        <p className="text-sm text-zinc-600" data-testid="clause-findings-no-critical">
          {t("clause.none_critical")}
        </p>
      )}

      {critical.map((clause, i) => (
        <ClauseCard key={`crit-${i}`} clause={clause} index={i} />
      ))}

      {others.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setShowOthers((v) => !v)}
            data-testid="clause-findings-toggle-others"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-600 hover:text-zinc-900 transition-colors"
          >
            {showOthers ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            {showOthers
              ? t("clause.hide_others")
              : t("clause.show_others", { count: others.length })}
          </button>
          {showOthers && others.map((clause, i) => (
            <ClauseCard key={`oth-${i}`} clause={clause} index={critical.length + i} />
          ))}
        </>
      )}
    </section>
  );
}
